import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Scissors } from "lucide-react";
import { useLang } from "../i18n";
import { HERO_IMG, isOpenNow } from "../siteData";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

function MaskedLine({ children, delay }) {
    return (
        <span className="block overflow-hidden pb-1">
            <motion.span
                className="block"
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
            >
                {children}
            </motion.span>
        </span>
    );
}

function HudClock() {
    const { t } = useLang();
    const [now, setNow] = useState(new Date());
    const [open, setOpen] = useState(isOpenNow());
    useEffect(() => {
        const iv = setInterval(() => {
            setNow(new Date());
            setOpen(isOpenNow());
        }, 1000);
        return () => clearInterval(iv);
    }, []);
    const pad = (n) => String(n).padStart(2, "0");
    return (
        <div
            data-testid="hero-hud-clock"
            className="inline-flex items-center gap-3 rounded-xl border border-white/10 bg-black/50 px-4 py-2.5 backdrop-blur-md"
        >
            <span className={`h-2 w-2 rounded-full ${open ? "bg-[#FF2E4D] pulse-dot" : "bg-[#8E8EA0]"}`} />
            <span className="font-mono2 text-sm tracking-widest text-[#FFB800]">
                {pad(now.getHours())}:{pad(now.getMinutes())}:{pad(now.getSeconds())}
            </span>
            <span className="font-mono2 text-[10px] tracking-[0.25em] text-[#8E8EA0] uppercase">
                9CUT {open ? t.hero.hudOpen : t.hero.hudClosed}
            </span>
        </div>
    );
}

export function Hero() {
    const { t } = useLang();
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
    const wmY = useTransform(scrollYProgress, [0, 1], [0, -80]);

    return (
        <section ref={ref} className="hero-radial relative min-h-screen overflow-hidden pt-32 pb-20" data-testid="hero-section">
            <motion.span
                style={{ y: wmY }}
                aria-hidden
                className="pointer-events-none absolute -top-8 left-1/2 -translate-x-1/2 select-none font-display text-[26vw] font-extrabold leading-none tracking-tighter text-stroke-amber opacity-40"
            >
                9CUT
            </motion.span>

            <div className="relative z-10 mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-[1.15fr_0.85fr]">
                <div>
                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.15 }}
                        className="mb-6 inline-flex items-center gap-2 rounded-full border border-[#FFB800]/30 bg-[#FFB800]/5 px-4 py-1.5 font-mono2 text-xs uppercase tracking-[0.25em] text-[#FFB800]"
                    >
                        <Scissors size={13} />
                        {t.hero.badge}
                    </motion.p>

                    <h1 className="font-display text-4xl font-extrabold uppercase leading-[1.05] tracking-tight sm:text-5xl lg:text-7xl">
                        <MaskedLine delay={0.3}>{t.hero.l1}</MaskedLine>
                        <MaskedLine delay={0.45}>
                            <span className="text-[#FFB800]">{t.hero.l2}</span>
                        </MaskedLine>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.75 }}
                        className="mt-6 max-w-xl text-base leading-relaxed text-[#8E8EA0] sm:text-lg"
                    >
                        {t.hero.sub}
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.9 }}
                        className="mt-8 flex flex-wrap items-center gap-4"
                    >
                        <button
                            data-testid="hero-book-now-btn"
                            onClick={() => scrollTo("booking")}
                            className="group inline-flex items-center gap-2 rounded-full bg-[#FFB800] px-7 py-3.5 font-semibold text-[#0B0B0E] transition-all duration-300 hover:bg-[#FF2E4D] hover:text-white hover:shadow-[0_0_40px_rgba(255,46,77,0.4)]"
                        >
                            {t.hero.cta1}
                            <ArrowDown size={16} className="transition-transform duration-300 group-hover:translate-y-0.5" />
                        </button>
                        <button
                            onClick={() => scrollTo("services")}
                            className="rounded-full border border-white/20 px-7 py-3.5 text-[#F4F4F6] transition-colors duration-300 hover:border-[#FFB800] hover:text-[#FFB800]"
                        >
                            {t.hero.cta2}
                        </button>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.8, delay: 1.1 }}
                        className="mt-10 flex flex-wrap items-center gap-6"
                    >
                        <HudClock />
                        <div className="flex gap-8">
                            {t.hero.stats.map((s) => (
                                <div key={s.l}>
                                    <p className="font-mono2 text-lg font-bold text-[#F4F4F6]">{s.v}</p>
                                    <p className="text-xs text-[#8E8EA0]">{s.l}</p>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                </div>

                <motion.div
                    style={{ y: imgY }}
                    initial={{ opacity: 0, scale: 0.94 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1.1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    className="relative hidden lg:block"
                >
                    <div className="glow-amber clip-frame relative overflow-hidden rounded-3xl border border-[#FFB800]/30">
                        <img
                            src={HERO_IMG}
                            alt="Midnight barber at work"
                            className="h-[560px] w-full object-cover"
                            loading="eager"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0E]/80 via-transparent to-transparent" />
                        <div className="absolute bottom-6 left-6">
                            <p className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#FFB800]">Midnight Session</p>
                            <p className="font-display text-xl font-bold text-white">21:00 — 04:00</p>
                        </div>
                    </div>
                    <div className="animate-float-slow absolute -right-4 top-8 rounded-2xl border border-white/10 bg-[#13131A]/90 px-5 py-4 backdrop-blur-xl">
                        <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#8E8EA0]">Next slot</p>
                        <p className="font-mono2 text-2xl font-bold text-[#FF2E4D]">21:00</p>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
